// Email Connection Status Widget
// Displays current Gmail connection state and management controls

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  RefreshCw, 
  Trash2,
  Mail,
  Clock,
  Wifi,
  WifiOff,
  AlertCircle
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import GmailOAuth from '@/components/auth/GmailOAuth';

/**
 * Email Connection Status Component
 * Shows connection state and provides management actions
 */
const EmailConnectionStatus = ({ 
  user, 
  connection, 
  onReconnect, 
  onDisconnect, 
  connectionHealth = 'unknown',
  onTestConnection 
}) => {
  const [isReconnecting, setIsReconnecting] = React.useState(false);
  const [showOAuth, setShowOAuth] = React.useState(false);

  /**
   * Handle OAuth success for reconnection
   */
  const handleOAuthSuccess = async (oauthData) => {
    setIsReconnecting(true);
    try {
      await onReconnect(oauthData);
      setShowOAuth(false);
    } catch (error) {
      console.error('OAuth reconnection failed:', error);
    } finally {
      setIsReconnecting(false);
    }
  };

  /**
   * Handle OAuth error
   */
  const handleOAuthError = (error) => {
    console.error('OAuth error:', error);
    setShowOAuth(false);
  };

  /**
   * Format last sync date
   */
  const formatLastSyncDate = (date) => {
    if (!date) return 'Never';
    
    const syncDate = new Date(date);
    const now = new Date();
    const diffMs = now - syncDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return syncDate.toLocaleDateString();
  };

  /**
   * Get connection status badge
   */
  const getStatusBadge = () => {
    if (!connection) {
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Not Connected
        </Badge>
      );
    }

    if (connectionHealth === 'error' || connection.error) {
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Connection Issue
        </Badge>
      );
    }

    if (connectionHealth === 'warning') {
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Warning
        </Badge>
      );
    }

    return (
      <Badge variant="default" className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Connected
      </Badge>
    );
  };

  /**
   * Get health indicator
   */
  const getHealthIndicator = () => {
    switch (connectionHealth) {
      case 'healthy':
        return (
          <div className="flex items-center text-green-600">
            <Wifi className="h-4 w-4 mr-1" />
            <span className="text-sm">Healthy</span>
          </div>
        );
      case 'warning':
        return (
          <div className="flex items-center text-yellow-600">
            <WifiOff className="h-4 w-4 mr-1" />
            <span className="text-sm">Warning</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center text-red-600">
            <WifiOff className="h-4 w-4 mr-1" />
            <span className="text-sm">Error</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center text-gray-600">
            <WifiOff className="h-4 w-4 mr-1" />
            <span className="text-sm">Unknown</span>
          </div>
        );
    }
  };

  // Show OAuth component if reconnecting
  if (showOAuth) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Reconnect Gmail Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <GmailOAuth
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            buttonText="Reconnect Gmail Account"
            showConsentMessage={true}
          />
          <div className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowOAuth(false)}
              disabled={isReconnecting}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Gmail Connection
          </div>
          {getStatusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection State Display */}
        {!connection ? (
          // Not Connected State
          <div className="text-center py-6">
            <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Email Connected
            </h3>
            <p className="text-gray-600 mb-6">
              Connect your Gmail to automatically track camp registrations
            </p>
            <Button 
              onClick={() => setShowOAuth(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Connect Gmail Account
            </Button>
          </div>
        ) : (
          // Connected State
          <div className="space-y-4">
            {/* Connection Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Gmail Connected</span>
                </div>
                <div className="text-sm text-gray-600 ml-6">
                  {connection.email || 'Email address not available'}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">Last Sync</span>
                </div>
                <div className="text-sm text-gray-600 ml-6">
                  {formatLastSyncDate(connection.last_sync_date)}
                </div>
              </div>
            </div>

            {/* Sync Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">
                  Daily sync: {connection.sync_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {getHealthIndicator()}
            </div>

            {/* Statistics */}
            {connection.total_camps_found > 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <BarChart3 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Total camps found: {connection.total_camps_found}
                </span>
              </div>
            )}

            {/* Error State */}
            {connection.error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {connection.error}
                </AlertDescription>
              </Alert>
            )}

            {/* Connection Health Warning */}
            {connectionHealth === 'warning' && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Connection may be experiencing issues. Try reconnecting or test the connection.
                </AlertDescription>
              </Alert>
            )}

            {/* Management Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOAuth(true)}
                disabled={isReconnecting}
              >
                {isReconnecting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Reconnect
              </Button>

              {onTestConnection && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onTestConnection}
                >
                  <Wifi className="h-4 w-4 mr-2" />
                  Test Connection
                </Button>
              )}

              <Button
                variant="destructive"
                size="sm"
                onClick={onDisconnect}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailConnectionStatus;
